import React, { useState } from "react";
import CategoryTable from "./CategoryTable";
import { useTheme } from "../contexts/ThemeContext";
import { FaPlus } from "react-icons/fa";

/**
 * A category list with add, rename and delete. Used for both expense categories
 * and income sources.
 *
 * Purely presentational: it owns the new-name input and the trim/duplicate
 * checks, and hands the caller a complete next list. Persistence and rollback
 * stay with Settings.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {string[]} props.categories
 * @param {string} props.placeholder
 * @param {(next: string[]) => void} props.onChange
 * @param {(message: string) => void} [props.onError]
 */
const CategoryManager = ({ title, categories, placeholder, onChange, onError }) => {
  const { theme } = useTheme();
  const gradient = theme.colors.gradient;
  const [newCategory, setNewCategory] = useState("");

  const handleAdd = () => {
    const trimmed = newCategory.trim();
    if (trimmed === "") return;
    if (categories.includes(trimmed)) {
      onError?.(`"${trimmed}" already exists.`);
      return;
    }
    onChange([...categories, trimmed]);
    setNewCategory("");
  };

  const handleSave = (oldCategory, updatedCategory) => {
    const trimmed = updatedCategory.trim();
    if (trimmed === "" || trimmed === oldCategory) return;
    onChange(categories.map((cat) => (cat === oldCategory ? trimmed : cat)));
  };

  const handleDelete = (category) => {
    onChange(categories.filter((cat) => cat !== category));
  };

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 mb-4">
      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        {title}
      </h4>
      <CategoryTable
        categories={categories}
        onSave={handleSave}
        onDelete={handleDelete}
      />
      <div className="flex gap-2 mt-3">
        <input
          type="text"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder={placeholder}
          className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all"
        />
        <button
          onClick={handleAdd}
          className={`p-3 px-5 rounded-xl text-white font-medium bg-gradient-to-r ${gradient.button} ${gradient.buttonHover} shadow-md ${theme.colors.shadow} transition-all active:scale-95 flex items-center gap-2`}
        >
          <FaPlus size={12} />
          Add
        </button>
      </div>
    </div>
  );
};

export default CategoryManager;
